import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign,
  Users,
  Home,
  Globe,
  User,
  ShoppingBag,
  Megaphone,
  Tag,
  MessageCircle,
  Bell,
  LayoutGrid,
  TrendingUp,
  Compass,
  UserCircle,
  Settings,
  LogIn,
  ChevronDown,
  LogOut,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useSwipeNavigation } from '../../hooks/useSwipeNavigation';
import { getNotifications } from '../../lib/api-client';
import resolveAvatar from '../../lib/resolveAvatar';
import { SubPanelSwiper, BottomNav, SubPanelIndicator } from './Navigation';
import type { SectionConfig, SectionId } from './Navigation/Navigation.types';

// Lightweight mobile components (replaces heavy desktop FeedClient/CommunityList/ProfileClient)
import { MobileFeed } from './MobileFeed';
import { MobileCommunityList } from './MobileCommunityList';
import { MobileProfile } from './MobileProfile';

// Mobile-specific panels (for sections without dedicated components)
import { FriendsPanel, MessagesPanel, ActivityPanel } from './panels/SocialPanels';
import { MobileMessageThread } from './MobileMessageThread';
import { MobileProfileOverlay } from './MobileProfileOverlay';
import { MobileCommunityOverlay } from './MobileCommunityOverlay';
import { MobilePostDetailOverlay } from './MobilePostDetailOverlay';
import { MobilePostViewOverlay } from './MobilePostViewOverlay';
import { MobileForumPostViewOverlay } from './MobileForumPostViewOverlay';

// ---------------------------------------------------------------------------
// Section configurations
// ---------------------------------------------------------------------------

const SECTIONS: SectionConfig[] = [
  {
    id: 'commerce',
    label: 'Commerce',
    icon: DollarSign,
    subPanels: [
      { id: 'marketplace', icon: ShoppingBag, label: 'Marketplace' },
      { id: 'ads', icon: Megaphone, label: 'Ads' },
      { id: 'sell', icon: Tag, label: 'Sell' },
    ],
    defaultSubPanel: 1,
  },
  {
    id: 'social',
    label: 'Social',
    icon: Users,
    subPanels: [
      { id: 'friends', icon: Users, label: 'Friends' },
      { id: 'messages', icon: MessageCircle, label: 'Messages' },
      { id: 'activity', icon: Bell, label: 'Activity' },
    ],
    defaultSubPanel: 1,
  },
  {
    id: 'feed',
    label: 'Home',
    icon: Home,
    subPanels: [
      { id: 'feed-left', icon: LayoutGrid, label: 'Share' },
      { id: 'feed-center', icon: Home, label: 'Feed' },
      { id: 'feed-right', icon: TrendingUp, label: 'Widgets' },
    ],
    defaultSubPanel: 1,
  },
  {
    id: 'communities',
    label: 'Groups',
    icon: Globe,
    subPanels: [
      { id: 'community-list', icon: Compass, label: 'Discover' },
      { id: 'community-feed', icon: Globe, label: 'Feed' },
      { id: 'community-right', icon: TrendingUp, label: 'Widgets' },
    ],
    defaultSubPanel: 1,
  },
  {
    id: 'you',
    label: 'You',
    icon: User,
    subPanels: [
      { id: 'profile-info', icon: UserCircle, label: 'Info' },
      { id: 'profile-main', icon: User, label: 'Profile' },
      { id: 'profile-settings', icon: Settings, label: 'Settings' },
    ],
    defaultSubPanel: 1,
  },
];

// ---------------------------------------------------------------------------
// Section slide animation
// ---------------------------------------------------------------------------

const sectionTransition = {
  type: 'spring' as const,
  damping: 26,
  stiffness: 260,
  mass: 0.9,
};

// ---------------------------------------------------------------------------
// Placeholder panel for sections not yet wired
// ---------------------------------------------------------------------------

function PlaceholderPanel({ label, icon: Icon }: { label: string; icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }> }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 p-6">
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center"
        style={{ background: 'var(--card-bg, rgba(255,255,255,0.06))' }}
      >
        <Icon size={28} style={{ color: 'var(--color-accent, #06b6d4)' }} />
      </div>
      <span className="text-lg font-semibold" style={{ color: 'var(--text-color, #fff)' }}>
        {label}
      </span>
      <span className="text-sm text-center max-w-[240px]" style={{ color: 'var(--text-color, #fff)', opacity: 0.5 }}>
        Coming soon
      </span>
    </div>
  );
}

function buildPlaceholders(section: SectionConfig): [React.ReactNode, React.ReactNode, React.ReactNode] {
  return section.subPanels.map((p) => (
    <PlaceholderPanel key={p.id} label={p.label} icon={p.icon} />
  )) as [React.ReactNode, React.ReactNode, React.ReactNode];
}

// Sections that render their own SubPanelSwiper internally
const MOBILE_COMPONENT_SECTIONS: Set<SectionId> = new Set(['feed', 'communities', 'you']);

// ---------------------------------------------------------------------------
// MobileShell
// ---------------------------------------------------------------------------

export function MobileShell() {
  const { user, logout, setShowLoginModal } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const nav = useSwipeNavigation({ sections: SECTIONS, initialSection: 2 });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);

  // Fix #7: Only sync section→URL when user taps bottom nav (not on mount/URL-driven changes)
  const userInitiatedNavRef = useRef(false);
  const handleSectionChange = useCallback((index: number) => {
    userInitiatedNavRef.current = true;
    nav.goToSection(index);
  }, [nav]);

  // Fix #3: Profile theme for header — listen for theme vars from MobileProfile
  const [profileHeaderTheme, setProfileHeaderTheme] = useState<React.CSSProperties>({});
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) setProfileHeaderTheme(detail);
    };
    window.addEventListener('mobileProfileThemeReady', handler);
    return () => window.removeEventListener('mobileProfileThemeReady', handler);
  }, []);

  // Apply/remove profile theme CSS vars on header via DOM API (reliable cleanup)
  const THEME_CSS_VARS = ['--text-color', '--color-accent', '--primary-color', '--secondary-color', '--card-bg', '--navbar-bg', '--tab-bar-bg', '--app-bg'];
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    if (nav.currentSection === 4 && Object.keys(profileHeaderTheme).length > 0) {
      for (const [key, value] of Object.entries(profileHeaderTheme)) {
        if (key.startsWith('--')) el.style.setProperty(key, value as string);
      }
    } else {
      for (const prop of THEME_CSS_VARS) {
        el.style.removeProperty(prop);
      }
    }
  }, [nav.currentSection, profileHeaderTheme]);

  // Lazy-mount: only render heavy sections once they've been visited.
  // Start with the initial section (feed = index 2) already mounted.
  const [mountedSections, setMountedSections] = useState<Set<number>>(() => new Set([2]));
  useEffect(() => {
    setMountedSections(prev => {
      if (prev.has(nav.currentSection)) return prev;
      const next = new Set(prev);
      next.add(nav.currentSection);
      return next;
    });
  }, [nav.currentSection]);

  // Fetch unread notification count for the bell badge
  useEffect(() => {
    if (!user) { setUnreadNotifCount(0); return; }
    (async () => {
      try {
        const data = await getNotifications(1);
        setUnreadNotifCount(data.unreadCount || 0);
      } catch { /* silent */ }
    })();
  }, [user]);

  // URL-based section routing: switch to correct section based on current URL
  // Runs on mount AND whenever location.pathname changes (refresh, navigate() calls)
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/u/') || path.startsWith('/user/') || path.startsWith('/profile')) {
      nav.goToSection(4); // "you" section
      nav.setSubPanel('you', 1); // center panel (profile)
    } else if (path.startsWith('/messages')) {
      nav.goToSection(1); // "social" section
      nav.setSubPanel('social', 1); // messages panel
    } else if (path.startsWith('/community')) {
      nav.goToSection(3); // "communities" section
    } else if (path.startsWith('/marketplace') || path.startsWith('/ads')) {
      nav.goToSection(0); // "commerce" section
    } else if (path.startsWith('/friends')) {
      nav.goToSection(1); // "social" section
      nav.setSubPanel('social', 0); // friends panel
    } else if (path.startsWith('/settings')) {
      nav.goToSection(4); // "you" section
      nav.setSubPanel('you', 2); // settings panel
    }
    // Default: stay on feed (index 2) — set by initialSection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Sync section changes → URL (so refresh/back works correctly)
  // Only update URL when section changes via bottom nav tap (not from URL-driven routing or mount)
  useEffect(() => {
    // Fix #7: Only sync URL when the user actively tapped the bottom nav
    if (!userInitiatedNavRef.current) return;
    userInitiatedNavRef.current = false;

    const sectionId = SECTIONS[nav.currentSection]?.id;
    const path = location.pathname;

    // Map section to its canonical URL
    const sectionUrls: Record<string, string | null> = {
      commerce: '/marketplace',
      social: '/friends',
      feed: '/feed',
      communities: '/community',
      you: user ? `/u/${user.username}` : null,
    };

    const targetUrl = sectionUrls[sectionId];
    if (!targetUrl) return;

    // Don't update if already on a sub-route of this section (e.g. /messages/123)
    if (path === targetUrl) return;
    if (sectionId === 'social' && path.startsWith('/messages/')) return;
    if (sectionId === 'communities' && path.startsWith('/community/')) return;
    if (sectionId === 'you' && (path.startsWith('/u/') || path.startsWith('/user/'))) return;

    navigate(targetUrl, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav.currentSection]);

  // Convert auth user to component-compatible format
  const feedCurrentUser = useMemo(() => {
    if (!user) return null;
    return {
      id: String(user.id),
      username: user.username,
      displayName: user.displayName || user.profileName || user.name || user.username,
      avatar: user.profilePicture || user.image || user.avatar || undefined,
      isSiteAdmin: user.isSiteAdmin,
      isSuperAdmin: user.isSuperAdmin,
      isSiteModerator: user.isSiteModerator,
    };
  }, [user]);

  // Build sub-panel content for simple sections (commerce, social)
  const sectionPanels = useMemo(() => {
    const map: Partial<Record<SectionId, [React.ReactNode, React.ReactNode, React.ReactNode]>> = {};

    const commerce = SECTIONS.find(s => s.id === 'commerce')!;
    map.commerce = buildPlaceholders(commerce);

    map.social = [
      <FriendsPanel key="friends" />,
      <MessagesPanel key="messages" />,
      <ActivityPanel key="activity" />,
    ];

    return map as Record<SectionId, [React.ReactNode, React.ReactNode, React.ReactNode]>;
  }, []);

  const activeSection = nav.activeSectionConfig;

  // Detect message thread overlay: /messages/:id
  const messageThreadId = useMemo(() => {
    const match = location.pathname.match(/^\/messages\/(.+)$/);
    return match ? match[1] : null;
  }, [location.pathname]);

  // Detect profile overlay: /u/:username or /user/:username (for OTHER users, not self)
  const profileOverlayUsername = useMemo(() => {
    const match = location.pathname.match(/^\/(?:u|user)\/([^/]+)$/);
    if (!match) return null;
    const targetUsername = match[1];
    // Only show overlay for OTHER users, not yourself
    if (user && targetUsername === user.username) return null;
    return targetUsername;
  }, [location.pathname, user]);

  // Detect community overlay: /community/:slug (specific community, not the list)
  const communityOverlaySlug = useMemo(() => {
    const match = location.pathname.match(/^\/community\/([^/]+)$/);
    return match ? match[1] : null;
  }, [location.pathname]);

  // Detect user profile post: /u/:username/:slug  or  /user/:username/:slug
  const postDetailParams = useMemo(() => {
    const match = location.pathname.match(/^\/(?:u|user)\/([^/]+)\/([^/]+)$/);
    return match ? { username: match[1], slug: match[2] } : null;
  }, [location.pathname]);

  // Detect forum post: /community/:communitySlug/forum/:postSlug  (check before community post)
  const forumPostParams = useMemo(() => {
    const match = location.pathname.match(/^\/community\/([^/]+)\/forum\/([^/]+)$/);
    return match ? { communitySlug: match[1], postSlug: match[2] } : null;
  }, [location.pathname]);

  // Detect community post: /community/:communitySlug/:postSlug  (not forum, not bare community page)
  const communityPostParams = useMemo(() => {
    // Exclude forum posts (/community/:slug/forum/:postSlug)
    if (location.pathname.match(/^\/community\/[^/]+\/forum\/[^/]+$/)) return null;
    const match = location.pathname.match(/^\/community\/([^/]+)\/([^/]+)$/);
    return match ? { communitySlug: match[1], postSlug: match[2] } : null;
  }, [location.pathname]);

  // Use the CSS variable directly so updates from handleSave are instant
  // ThemeContext and handleSave both set --app-bg on document.documentElement
  const shellBackground = 'var(--app-bg, #0a0a0a)';

  return (
    <div
      className="fixed inset-0 flex flex-col overflow-hidden"
      style={{ background: shellBackground }}
    >
      {/* ── Header area: section title + sub-panel dots ── */}
      <header
        ref={headerRef}
        className="flex-shrink-0"
        style={{
          background: 'var(--navbar-bg)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          zIndex: 50,
          paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)',
        }}
      >
        <div className="flex items-center justify-between px-5 pt-2 pb-1">
          {/* Left: EarthSocial title — flex-1 for centering */}
          <div className="flex-1" style={{ marginLeft: 4 }}>
            <span className="text-lg font-bold" style={{ color: 'var(--text-color, #fff)' }}>
              Earth<span style={{ color: '#E91E63' }}>Social</span>
            </span>
          </div>

          {/* Center: Section label */}
          <AnimatePresence mode="wait">
            <motion.h1
              key={activeSection.id}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.15 }}
              className="text-base font-bold tracking-wide"
              style={{ color: 'var(--text-color, #fff)' }}
            >
              {activeSection.label}
            </motion.h1>
          </AnimatePresence>

          {/* Right: Sign-in (logged out) or bell + avatar (logged in) — flex-1 for centering */}
          {!user ? (
            <div className="flex-1 flex justify-end">
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{
                  backgroundColor: 'var(--color-accent, #06b6d4)',
                  color: '#fff',
                }}
              >
                <LogIn size={14} />
                Sign In
              </button>
            </div>
          ) : (
            <div className="relative flex-1 flex justify-end items-center gap-4" style={{ marginRight: 12 }}>
              {/* Bell icon — tap navigates to Activity panel */}
              <button
                onClick={() => {
                  nav.goToSection(1); // social section
                  nav.setSubPanel('social', 2); // activity panel
                  setUnreadNotifCount(0);
                }}
                className="relative p-1.5"
              >
                <Bell size={20} style={{ color: 'var(--text-color, #fff)' }} />
                {unreadNotifCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center text-[10px] font-bold"
                    style={{ backgroundColor: '#ef4444', color: '#fff' }}
                  >
                    {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                  </span>
                )}
              </button>

              {/* User avatar + chevron — tap opens user menu */}
              <button
                onClick={() => setShowUserMenu(prev => !prev)}
                className="flex flex-col items-center"
              >
                {user.profilePicture || user.image || user.avatar ? (
                  <img
                    src={resolveAvatar(user.profilePicture || user.image || user.avatar) || ''}
                    alt=""
                    className="w-7 h-7 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: 'var(--color-accent, #06b6d4)', color: '#fff' }}
                  >
                    {(user.displayName || user.username || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <ChevronDown
                  size={12}
                  className="transition-transform mt-0.5"
                  style={{
                    color: 'var(--text-color, #fff)',
                    opacity: 0.5,
                    transform: showUserMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </button>

              {/* User dropdown menu */}
              <AnimatePresence>
                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-[99]"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-2 z-[100] min-w-[160px] rounded-lg overflow-hidden"
                      style={{
                        background: 'var(--navbar-bg, rgba(0,0,0,0.9))',
                        border: '1px solid rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(20px)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                      }}
                    >
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate(`/u/${user.username}`);
                        }}
                        className="flex items-center gap-2.5 w-full px-4 py-3 text-sm font-medium transition-colors"
                        style={{ color: 'var(--text-color, #fff)' }}
                      >
                        <User size={16} style={{ opacity: 0.7 }} />
                        My Profile
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/settings');
                        }}
                        className="flex items-center gap-2.5 w-full px-4 py-3 text-sm font-medium transition-colors"
                        style={{
                          color: 'var(--text-color, #fff)',
                          borderTop: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <Settings size={16} style={{ opacity: 0.7 }} />
                        Settings
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          logout?.();
                          navigate('/login');
                        }}
                        className="flex items-center gap-2.5 w-full px-4 py-3 text-sm font-medium transition-colors"
                        style={{
                          color: '#ef4444',
                          borderTop: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <LogOut size={16} style={{ opacity: 0.7 }} />
                        Sign Out
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        <SubPanelIndicator
          subPanels={activeSection.subPanels}
          active={nav.activeSubPanel}
          onTap={(i) => nav.setSubPanel(activeSection.id, i)}
        />
      </header>

      {/* ── Section content area ── */}
      <div className="flex-1 relative overflow-hidden">
        {SECTIONS.map((section, index) => {
          const isActive = index === nav.currentSection;
          const isMobileComponent = MOBILE_COMPONENT_SECTIONS.has(section.id);
          const isMounted = mountedSections.has(index);

          return (
            <motion.div
              key={section.id}
              initial={false}
              animate={{
                x: isActive ? '0%'
                  : index < nav.currentSection ? '-100%' : '100%',
                opacity: isActive ? 1 : 0,
              }}
              transition={sectionTransition}
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: isActive ? 'auto' : 'none',
                willChange: isActive ? 'auto' : 'transform',
              }}
            >
              {/* Lazy-mount: don't render heavy components until section is first visited */}
              {!isMounted ? null : isMobileComponent ? (
                section.id === 'feed' ? (
                  <MobileFeed
                    currentUser={feedCurrentUser}
                    activePanel={nav.subPanelMap[section.id]}
                    onPanelChange={(i) => nav.setSubPanel(section.id, i)}
                    screenWidth={nav.screenWidth}
                  />
                ) : section.id === 'communities' ? (
                  <MobileCommunityList
                    currentUser={feedCurrentUser}
                    activePanel={nav.subPanelMap[section.id]}
                    onPanelChange={(i) => nav.setSubPanel(section.id, i)}
                    screenWidth={nav.screenWidth}
                  />
                ) : section.id === 'you' ? (
                  <MobileProfile
                    activePanel={nav.subPanelMap[section.id]}
                    onPanelChange={(i) => nav.setSubPanel(section.id, i)}
                    screenWidth={nav.screenWidth}
                  />
                ) : null
              ) : (
                <SubPanelSwiper
                  activePanel={nav.subPanelMap[section.id]}
                  onPanelChange={(i) => nav.setSubPanel(section.id, i)}
                  screenWidth={nav.screenWidth}
                >
                  {sectionPanels[section.id]}
                </SubPanelSwiper>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* ── Floating pill bottom nav ── */}
      <BottomNav
        sections={SECTIONS}
        currentSection={nav.currentSection}
        onSectionChange={handleSectionChange}
      />

      {/* ── Message thread overlay ── */}
      {messageThreadId && (
        <MobileMessageThread conversationId={messageThreadId} />
      )}

      {/* ── Profile overlay (viewing other users) ── */}
      {profileOverlayUsername && (
        <MobileProfileOverlay username={profileOverlayUsername} />
      )}

      {/* ── Community overlay (viewing specific community) ── */}
      {communityOverlaySlug && (
        <MobileCommunityOverlay slug={communityOverlaySlug} />
      )}

      {/* ── Post overlays (above profile/community overlays at z-[70]) ── */}
      {postDetailParams && (
        <MobilePostDetailOverlay
          username={postDetailParams.username}
          slug={postDetailParams.slug}
        />
      )}
      {communityPostParams && (
        <MobilePostViewOverlay
          communitySlug={communityPostParams.communitySlug}
          postSlug={communityPostParams.postSlug}
        />
      )}
      {forumPostParams && (
        <MobileForumPostViewOverlay
          communitySlug={forumPostParams.communitySlug}
          postSlug={forumPostParams.postSlug}
        />
      )}
    </div>
  );
}
